'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { useTranslation } from '@/hooks/useTranslation';

const EgyptianCottonStory = () => {
  const { t } = useTranslation();

  return (
    <section className="py-24 bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-6 py-2 bg-amber-600/10 border border-amber-600/30 text-amber-800 rounded-full text-sm font-semibold tracking-widest uppercase mb-6">
            {t('theGoldStandard')}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6 font-serif">
            {t('egyptianLongStapleCotton')}
          </h2>
          <p className="text-xl text-stone-700 max-w-3xl mx-auto">
            {t('egyptianCottonDesc')}
          </p>
        </div>

        {/* Cotton Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <CottonFeature
            icon="SparklesIcon"
            title={t('extraLongStaple')}
            description={t('extraLongStapleDesc')}
          />
          <CottonFeature
            icon="ShieldCheckIcon"
            title={t('superiorStrength')}
            description={t('superiorStrengthDesc')}
          />
          <CottonFeature
            icon="SunIcon"
            title={t('naturalLuster')}
            description={t('naturalLusterDesc')}
          />
        </div>

        {/* Story Content */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-amber-100">
          <div className="grid lg:grid-cols-2">
            {/* Image Side */}
            <div className="relative h-96 lg:h-auto">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: "url('/images/heritage/cotton-field.jpg')",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20" />
            </div>

            {/* Content Side */}
            <div className="p-12 flex flex-col justify-center">
              <h3 className="text-3xl font-bold text-stone-900 mb-6 font-serif">
                {t('giftFromNile')}
              </h3>

              <div className="space-y-6 text-stone-700 text-lg leading-relaxed">
                <p>
                  {t('giftFromNileDesc1')}
                </p>

                <p>
                  {t('giftFromNileDesc2')}
                </p>

                <p className="font-semibold text-amber-800">
                  {t('giftFromNileDesc3')}
                </p>
              </div>

              <div className="mt-8 flex gap-4">
                <a
                  href="/quality-standards"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors duration-200 shadow-lg"
                >
                  <Icon name="DocumentCheckIcon" size={20} variant="solid" />
                  <span>{t('qualityStandards')}</span>
                </a>
                <a
                  href="/manufacturer-directory"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-stone-100 text-stone-900 border-2 border-stone-300 rounded-lg font-semibold hover:bg-stone-200 transition-colors duration-200"
                >
                  <span>{t('meetOurManufacturers')}</span>
                  <Icon name="ArrowRightIcon" size={20} variant="outline" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          <StatCard
            number="3000+"
            label={t('yearsOfHeritage')}
            icon="ClockIcon"
          />
          <StatCard
            number="1.5-2″"
            label={t('fiberLength')}
            icon="ArrowsPointingOutIcon"
          />
          <StatCard
            number="100%"
            label={t('naturalPure')}
            icon="CheckBadgeIcon"
          />
          <StatCard
            number="#1"
            label={t('globalQuality')}
            icon="TrophyIcon"
          />
        </div>
      </div>
    </section>
  );
};

// Cotton Feature Card
const CottonFeature = ({
  icon,
  title,
  description,
}: {
  icon: string;
  title: string;
  description: string;
}) => {
  return (
    <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-amber-100 hover:shadow-xl hover:border-amber-200 transition-all duration-300">
      <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center shadow-lg">
        <Icon name={icon as any} size={28} variant="solid" className="text-white" />
      </div>
      <h3 className="text-xl font-bold text-stone-900 mb-3">{title}</h3>
      <p className="text-stone-600 leading-relaxed">{description}</p>
    </div>
  );
};

// Stat Card
const StatCard = ({
  number,
  label,
  icon,
}: {
  number: string;
  label: string;
  icon: string;
}) => {
  return (
    <div className="text-center p-6 bg-gradient-to-br from-amber-600 to-amber-700 rounded-xl shadow-lg text-white">
      <Icon name={icon as any} size={24} variant="solid" className="mx-auto mb-3 opacity-80" />
      <div className="text-3xl font-bold mb-1">{number}</div>
      <div className="text-sm font-medium opacity-90">{label}</div>
    </div>
  );
};

export default EgyptianCottonStory;
